import { useState } from "react";
import { useAllTeams, useWorkerTeams } from "../../hooks/useTeamData";
import { useAdminAccess } from "../../hooks/useAdminAccess";

export type TeamSelection = 
  | { type: 'individual' }
  | { type: 'team', teamId: number, teamName: string }
  | { type: 'all' }
  | { type: 'all_my_teams' };

interface TeamSelectorProps {
  onSelectionChange: (selection: TeamSelection) => void;
  currentSelection: TeamSelection;
  className?: string;
  workerId?: number | null;
  hideIndividual?: boolean;
}

const TeamSelector: React.FC<TeamSelectorProps> = ({ 
  onSelectionChange, 
  currentSelection, 
  className = "",
  workerId = null,
  hideIndividual = false
}) => {
  const { hasAccess, hasOwnerAccess, roleId, loading: accessLoading } = useAdminAccess();
  // Fetch both (optimistic), rely on roleId to decide usage.
  // Note: useWorkerTeams expects a number | null.
  const { teams: allTeams, loading: allTeamsLoading, error: allTeamsError } = useAllTeams();
  const { teams: workerTeams, loading: workerTeamsLoading, error: workerTeamsError } = useWorkerTeams(workerId);

  const isAdminOrOwner = roleId === 1 || roleId === 2;
  const isTeamLead = roleId === 3;

  const teams = isAdminOrOwner ? allTeams : (isTeamLead ? workerTeams : []);
  const teamsLoading = isAdminOrOwner ? allTeamsLoading : (isTeamLead ? workerTeamsLoading : false);
  const teamsError = isAdminOrOwner ? allTeamsError : (isTeamLead ? workerTeamsError : null);
  
  const [isOpen, setIsOpen] = useState(false);

  // Don't render if user doesn't have access
  if (accessLoading || (!hasAccess && !hasOwnerAccess && roleId !== 3)) {
    return null;
  }

  const loading = teamsLoading;
  const error = teamsError;

  const handleSelectionChange = (selection: TeamSelection) => {
    onSelectionChange(selection);
    setIsOpen(false);
  };

  const getDisplayText = () => {
    switch (currentSelection.type) {
      case 'individual':
        return 'Mein Umsatz';
      case 'team':
        return currentSelection.teamName;
      case 'all':
        return 'Alle Teams';
      case 'all_my_teams':
        return 'Alle meine Teams';
      default:
        return 'Auswahl';
    }
  };

  const getDropdownOptions = () => {
    const options = [];
    
    // All my teams option (only for role_id 3, and only if they have teams)
    if (isTeamLead && teams.length > 0) {
      options.push({
        label: 'Alle meine Teams',
        value: { type: 'all_my_teams' as const },
        isActive: currentSelection.type === 'all_my_teams'
      });
    }

    // Individual option (hidden when hideIndividual is set)
    if (!hideIndividual) {
      options.push({
        label: 'Mein Umsatz',
        value: { type: 'individual' as const },
        isActive: currentSelection.type === 'individual'
      });
    }

    // Team options
    if (teams.length > 0) {
      teams.forEach(team => {
        options.push({
          label: team.name,
          value: { type: 'team' as const, teamId: team.id, teamName: team.name },
          isActive: currentSelection.type === 'team' && currentSelection.teamId === team.id
        });
      });
    }

    // All teams option (only for role_id 1 & 2)
    if (isAdminOrOwner) {
      options.push({
        label: 'Alle Teams',
        value: { type: 'all' as const },
        isActive: currentSelection.type === 'all'
      });
    }

    return options;
  };

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <div className="animate-pulse">
          <div className="h-10 w-48 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`relative ${className}`}>
        <div className="text-sm text-red-500">
          Fehler beim Laden der Teams
        </div>
      </div>
    );
  }

  const options = getDropdownOptions();

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-lg bg-white px-4 py-2.5 text-left text-sm font-medium text-gray-900 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white dark:ring-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-600 sm:min-w-[200px]"
      >
        <span className="truncate">{getDisplayText()}</span>
        <svg
          className={`ml-2 h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 20 20"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 z-20 mt-2 w-full min-w-[200px] rounded-lg bg-white shadow-lg ring-1 ring-gray-300 dark:bg-gray-800 dark:ring-gray-600">
            <div className="py-1">
              {options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectionChange(option.value)}
                  className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    option.isActive
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                  {option.isActive && (
                    <svg
                      className="ml-2 inline h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TeamSelector;